<template>
  <div class="editable-cell-wrapper">
    <span
      :id="cellId"
      ref="cellTrigger"
      class="editable-cell"
      :class="{ 'read-only': readOnly }"
      @click="openEditor"
    >
      {{ displayValue }}
    </span>
    <b-popover
      v-if="popoverVisible"
      :target="cellId"
      :manual="true"
      v-model="popoverVisible"
      placement="top"
      container="body"
      custom-class="cell-editor-popover"
      @hidden="onPopoverHidden"
    >
      <template #title>
        {{ fieldLabel }}
      </template>
      <div class="cell-editor-content">
        <multiselect-choices
          v-if="usesChoicePicker"
          ref="choicePicker"
          v-model="editValue"
          :choices="choices"
          :selection-mode="selectionMode"
          :label="fieldLabel"
          :radio-name="cellId"
          class="mb-2"
          @keydown.enter.prevent="save"
        />
        <b-form-input
          v-else-if="fieldType !== 'dateTime'"
          ref="editInput"
          v-model="editValue"
          :type="fieldType === 'password' ? 'password' : 'text'"
          :autocomplete="fieldType === 'password' ? 'new-password' : 'off'"
          size="sm"
          class="mb-2"
          @keyup.enter="save"
        />
        <b-form-input
          v-else
          ref="editInput"
          v-model="editValue"
          :type="dateOnly ? 'date' : 'datetime-local'"
          size="sm"
          class="mb-2"
          @keyup.enter="save"
        />
        <div class="d-flex justify-content-end">
          <b-button size="sm" variant="secondary" class="mr-1 me-1" @click="cancel">Cancel</b-button>
          <b-button size="sm" variant="primary" @click="save">Save</b-button>
        </div>
      </div>
    </b-popover>
  </div>
</template>

<script>
import { formatMultiselectDisplay } from '../lib/multiselect-display.js'
import { hasChoices } from '../lib/multiselect-cell.js'
import { formatCalendarDate, formatInstant, isCalendarDate, toDateTimeLocal } from '../../../shared/app-timezone'
import MultiselectChoices from './MultiselectChoices.vue'

export default {
  name: 'EditableCell',
  components: {
    MultiselectChoices
  },
  props: {
    value: {
      type: [String, Number, Object, Array],
      default: ''
    },
    item: {
      type: Object,
      required: true
    },
    fieldKey: {
      type: String,
      required: true
    },
    fieldLabel: {
      type: String,
      default: ''
    },
    fieldType: {
      type: String,
      default: 'text'
    },
    choices: {
      type: Array,
      default: () => []
    },
    selectionMode: {
      type: String,
      default: 'multiple'
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    timeZone: {
      type: String,
      default: 'UTC'
    },
    dateOnly: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      popoverVisible: false,
      editValue: ''
    }
  },
  computed: {
    cellId () {
      const id = (this.item && this.item.id) != null ? this.item.id : Math.random().toString(36).slice(2)
      return `cell-${id}-${this.fieldKey}`
    },
    usesChoicePicker () {
      return this.fieldType === 'multiselect' && hasChoices(this.choices)
    },
    displayValue () {
      const v = this.value
      if (this.fieldType === 'password') return (v == null || v === '') ? '—' : '••••••••'
      if (v == null || v === '') return '—'
      if (typeof v === 'object') {
        if (typeof v.then === 'function') return '…'
        try { return JSON.stringify(v) } catch { return '[object]' }
      }
      if (this.fieldType === 'multiselect') {
        const text = formatMultiselectDisplay(v)
        return text || '—'
      }
      if (this.dateOnly || (this.fieldType === 'dateTime' && isCalendarDate(v))) {
        return formatCalendarDate(v) || v
      }
      if (this.fieldType === 'dateTime' && typeof v === 'string') {
        return formatInstant(v, this.timeZone) || v
      }
      return String(v)
    }
  },
  mounted () {
    this.$nextTick(() => this.openEditor())
  },
  beforeUnmount () {
    // Safety net: clean up listener if still registered
    if (this._onCellEditorOpen) {
      document.removeEventListener('cell-editor-open', this._onCellEditorOpen)
    }
  },
  methods: {
    _registerCloseListener () {
      if (this._onCellEditorOpen) return // already registered
      this._onCellEditorOpen = (e) => {
        if (e.detail !== this.cellId && this.popoverVisible) {
          this.popoverVisible = false
          this._unregisterCloseListener()
        }
      }
      document.addEventListener('cell-editor-open', this._onCellEditorOpen)
    },
    _unregisterCloseListener () {
      if (this._onCellEditorOpen) {
        document.removeEventListener('cell-editor-open', this._onCellEditorOpen)
        this._onCellEditorOpen = null
      }
    },
    openEditor () {
      if (this.readOnly) return
      this._closed = false
      document.dispatchEvent(new CustomEvent('cell-editor-open', { detail: this.cellId }))
      const raw = this.value
      if (this.usesChoicePicker) {
        this.editValue = raw == null ? '' : raw
      } else {
        this.editValue = (raw == null || typeof raw === 'object') ? '' : String(raw)
        if (this.fieldType === 'multiselect') {
          this.editValue = formatMultiselectDisplay(raw)
        }
        if (this.dateOnly && raw) {
          this.editValue = String(raw).slice(0, 10)
        } else if (this.fieldType === 'dateTime' && raw) {
          this.editValue = isCalendarDate(String(raw)) ? String(raw).slice(0, 10) : toDateTimeLocal(raw, this.timeZone)
        }
      }
      this.popoverVisible = true
      this._registerCloseListener()
      this.$nextTick(() => {
        if (this.usesChoicePicker && this.$refs.choicePicker) {
          this.$refs.choicePicker.focus()
          return
        }
        const input = this.$refs.editInput
        if (input && input.$el) {
          input.$el.focus()
        } else if (input) {
          input.focus()
        }
      })
    },
    save () {
      let valueToSave = this.editValue
      if (this.fieldType === 'number') {
        valueToSave = parseFloat(this.editValue)
        if (isNaN(valueToSave)) valueToSave = null
      } else if (this.fieldType === 'dateTime' && this.editValue) {
        valueToSave = this.editValue
      }
      this._closed = true
      this.$emit('save', {
        item: this.item,
        fieldKey: this.fieldKey,
        newValue: valueToSave
      })
      this.popoverVisible = false
      this._unregisterCloseListener()
    },
    cancel () {
      this.closeEditor()
    },
    closeEditor () {
      if (this._closed) return
      this._closed = true
      this.popoverVisible = false
      this._unregisterCloseListener()
      this.$emit('cancel')
    },
    onPopoverHidden () {
      this.closeEditor()
    }
  }
}
</script>

<style scoped>
.editable-cell-wrapper {
  width: 100%;
}
.editable-cell {
  cursor: pointer;
  padding: 4px 8px;
  display: block;
  width: 100%;
  border-radius: 3px;
  min-height: 1.4em;
  transition: background-color 0.1s;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 300px;
}
.editable-cell:hover {
  background-color: rgba(154, 209, 43, 0.1);
  outline: 1px solid rgba(154, 209, 43, 0.3);
}
.editable-cell.read-only {
  cursor: default;
  color: #888;
}
.editable-cell.read-only:hover {
  background-color: transparent;
  outline: none;
}
.cell-editor-content {
  min-width: 240px;
}
</style>
